import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, paid, error

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${API}/payments/status/${sessionId}`,
          { withCredentials: true }
        );
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (data.status === "expired") {
          setStatus("error");
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus("error");
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus("error");
        }
      }
    };

    poll();
  }, [sessionId, refreshUser]);

  return (
    <div
      className="min-h-screen bg-gray-50/50 flex items-center justify-center p-6"
      data-testid="payment-success-page"
    >
      <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2
              size={48}
              className="mx-auto mb-4 text-indigo-500 animate-spin"
            />
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Verification du paiement...
            </h1>
            <p className="text-sm text-gray-500">
              Veuillez patienter quelques secondes.
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Paiement confirme !
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              Votre compte Premium est maintenant actif. Vous pouvez créer des
              diagrammes illimités.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm"
              data-testid="go-dashboard-btn"
            >
              Acceder au Dashboard
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-rose-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Erreur de paiement
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              Le paiement n'a pas pu etre confirme. Si vous avez ete debite,
              contactez notre support.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="bg-gray-900 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors shadow-sm"
              data-testid="go-back-btn"
            >
              Retour au Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
